import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  INDICATOR_REGION_ANALYSIS_SUMMARY,
  PAGE_SIZE_OPTIONS,
} from "../../../utils/constants";
import AnalysisFilterTemplate from "../component/AnalysisFilterTemplate";
import { useFilter } from "../../../hooks/filter/useFilter";
import { Option } from "../../../types/General/Option";
import SelectGroup from "../../../components/molecule/SelectGroup/SelectGroup";
import { getIndicatorLevel } from "../../../services/Indicator/getIndicatorLevel";
import { indicatorRegionAnalysisSelectFilterData } from "../data/Region/indicatorRegionAnalysisSelectFilterData";
import { getCountyById } from "../../../services/TabelasAuxiliares/getCountyById";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import { Box, Flex } from "@chakra-ui/react";
import { borderRadius, borderStyle } from "../../../utils/styles/styles";
import useFetchData from "../../../hooks/data/useFetchData";
import { getIndicatorRegion } from "../../../services/Indicator/getIndicatorRegion";
import TableRegion from "./TableRegion";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import useTableRendering from "../../../hooks/table/useTableRendering";
import { PaginationConfig } from "../../../types/Components/Table/PaginationConfig";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import { useCustomModal } from "../../../hooks/modal/useCustomModal";
import { getSubSystem } from "../../../services/Indicator/getSubSystem";
import { getIndicatorType } from "../../../services/Indicator/getIndicatorType";
import { FilterConfig } from "../../../types/Components/Table/FilterConfig";
import { regionTableSelectFilterData } from "../data/Region/regionTableSelectFilterData";
import { updateSelectedFilterItemsName } from "../../../helpers/updateSelectedFilterItemsName";
import AccordionComponent from "../../../components/atom/Accordion/AccordionComponent";
import RegionSearchTamplate from "./RegionSearchTamplate";
import RegionSearchTable from "./RegionSearchTable";
import { getIndicatorDataStates } from "../../../services/Indicator/getIndicatorDataStates";
import { renderLoadingState } from "../../../hooks/table/TableStates";
import { useAuth } from "../../../provider/AuthProvider";
import { applyRoleBasedFilters } from "../../../helpers/Filter/authUserFilter";
import { IndicatorFormValues } from "../../../types/Indicator/IndicatorDataReport/IndicatorFormValues";
import { useLocation } from "react-router-dom";

interface FilterState {
  IndicatorLevelId: Option | null;
  ProvinceId: Option | null;
  CountyId: Option | null;
  SubSystemId: Option | null;
  IndicatorTypeId: Option | null;
  StateId: Option | null;
}

interface OptionsState {
  levelOptions: Option[];
  provinceOptions: Option[];
  countyOptions: Option[];
  subSystemOptions: Option[];
  typeOptions: Option[];
  stateOptions: Option[];
}

const RegionAnalysis: FC = () => {
  const location = useLocation();
  const { auth } = useAuth();
  const user = auth.user;
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [errorFindingDataMonth, setErrorFindingDataMonth] = useState(false);
  const [errorFindingDataTrimester, setErrorFindingDataTrimester] =
    useState(false);
  const [errorFindingDataSemester, setErrorFindingDataSemester] =
    useState(false);
  const [errorFindingDataYear, setErrorFindingDataYear] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [dataTableMonth, setDataTableMonth] = useState<any[]>([]);
  const [dataTableTrimester, setDataTableTrimester] = useState<any[]>([]);
  const [dataTableSemester, setDataTableSemester] = useState<any[]>([]);
  const [dataTableYear, setDataTableYear] = useState<any[]>([]);
  const [totalItemsMonth, setTotalItemsMonth] = useState<number>(0);
  const [totalItemsTrimester, setTotalItemsTrimester] = useState<number>(0);
  const [totalItemsSemester, setTotalItemsSemester] = useState<number>(0);
  const [totalItemsYear, setTotalItemsYear] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [showTable, setShowTable] = useState<boolean>(false);
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);
  const [regionLoaded, setRegionLoaded] = useState(false);
  const [formValues, setFormValues] = useState<IndicatorFormValues>({
    frequency: null,
    province: null,
    county: null,
    indicator: null,
  });

  const receivedFilters = location.state?.filters;

  const [filterState, setFilterState] = useState<FilterState>({
    IndicatorLevelId:
      receivedFilters?.indicatorLevel ??
      (user?.role?.level?.id === 4
        ? { id: 4, name: "Provincial" }
        : user?.role?.level?.id === 5
          ? { id: 3, name: "Municipal" }
          : null),
    ProvinceId:
      receivedFilters?.province ??
      (user?.role?.level?.id >= 4 && user?.province
        ? { id: user.province.id, name: user.province.name }
        : null),
    CountyId:
      receivedFilters?.county ??
      (user?.role?.level?.id === 5 && user?.county
        ? { id: user.county.id, name: user.county.name }
        : null),
    SubSystemId: null,
    IndicatorTypeId: null,
    StateId: null,
  });
  const [optionsState, setOptionsState] = useState<OptionsState>({
    levelOptions: [],
    provinceOptions: [],
    countyOptions: [],
    subSystemOptions: [],
    typeOptions: [],
    stateOptions: [],
  });

  const { control, setValue, resetField } = useForm();
  const filterNames = useMemo(
    () => [
      "IndicatorLevelId",
      "ProvinceId",
      "CountyId",
      "SubSystemId",
      "IndicatorTypeId",
      "StateId",
      "IndicatorName",
    ],
    [],
  );

  useEffect(() => {
    const fetchData = async (filterValues?: any) => {
      setIsLoaded(false);
      setErrorFindingData(false);
      setErrorFindingDataMonth(false);
      setErrorFindingDataTrimester(false);
      setErrorFindingDataSemester(false);
      setErrorFindingDataYear(false);
      setRegionLoaded(false);

      const updatedFilters = applyRoleBasedFilters(
        user,
        filterValues,
        setFilter,
        true,
        receivedFilters,
      );

      try {
        const result = await getIndicatorRegion(updatedFilters);
        const allData = result.content || [];
        setDataTable(allData);

        const groupAndPaginate = (
          frequencyId: number,
          currentPage: number,
          setData: (data: any[]) => void,
          setTotal: (count: number) => void,
          setError: (val: boolean) => void,
        ) => {
          try {
            const grouped = allData.filter(
              (item) => item.indicator?.frequency.id === frequencyId,
            );

            const total = grouped.length;
            const paginated = grouped.slice(
              (currentPage - 1) * pageSize,
              currentPage * pageSize,
            );

            setData(paginated);
            setTotal(total);
          } catch (err) {
            console.error(
              `Grouping error for FrequencyId ${frequencyId}:`,
              err,
            );
            setError(true);
          }
        };

        groupAndPaginate(
          3,
          currentPageMonth,
          setDataTableMonth,
          setTotalItemsMonth,
          setErrorFindingDataMonth,
        );
        groupAndPaginate(
          4,
          currentPageTrimester,
          setDataTableTrimester,
          setTotalItemsTrimester,
          setErrorFindingDataTrimester,
        );
        groupAndPaginate(
          5,
          currentPageSemester,
          setDataTableSemester,
          setTotalItemsSemester,
          setErrorFindingDataSemester,
        );
        groupAndPaginate(
          6,
          currentPageYear,
          setDataTableYear,
          setTotalItemsYear,
          setErrorFindingDataYear,
        );
      } catch (error) {
        console.error("API Error:", error);
        setErrorFindingData(true);
      }

      setIsSearching(false);
      setTimeout(() => {
        setRegionLoaded(true);
        setIsLoaded(true);
      }, 3000);
    };

    if (isSearching) fetchData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  const { filters, setFilter } = useFilter({ setValue, filterNames });

  useEffect(() => {
    setShowTable(false);
  }, [
    filterState.IndicatorLevelId,
    filterState.ProvinceId,
    filterState.CountyId,
  ]);

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (filterState.ProvinceId) {
        getCountyById({ ParentId: filterState.ProvinceId.id })
          .then((result) => {
            setOptionsState((prev) => ({
              ...prev,
              countyOptions: result,
            }));
          })
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyLoaded(true);
        setOptionsState((prev) => ({ ...prev, countyOptions: [] }));
      }
    };
    updateCountyOptions();
  }, [filterState.ProvinceId]);

  const { isLoaded: levelLoaded } = useFetchData(getIndicatorLevel, (data) =>
    setOptionsState((prev) => ({ ...prev, levelOptions: data })),
  );
  const { isLoaded: provinceLoaded } = useFetchData(getProvince, (data) =>
    setOptionsState((prev) => ({ ...prev, provinceOptions: data })),
  );
  const { isLoaded: subSystemLoaded } = useFetchData(getSubSystem, (data) =>
    setOptionsState((prev) => ({ ...prev, subSystemOptions: data })),
  );
  const { isLoaded: typeLoaded } = useFetchData(getIndicatorType, (data) =>
    setOptionsState((prev) => ({ ...prev, typeOptions: data })),
  );
  const { isLoaded: stateLoaded } = useFetchData(
    getIndicatorDataStates,
    (data) => setOptionsState((prev) => ({ ...prev, stateOptions: data })),
  );

  const filtersArray = filterNames
    .map((filterName) => ({
      key: filterName,
      value: filters[filterName],
      setValue: (value: string | undefined) => setFilter(filterName, value),
    }))
    .filter((filter) => filter.value !== undefined);

  const keepActiveFiltersArray = filtersArray.filter((filter) =>
    ["IndicatorLevelId", "ProvinceId", "CountyId"].includes(filter.key),
  );

  const selectComponentsMainTable = indicatorRegionAnalysisSelectFilterData(
    optionsState.levelOptions,
    optionsState.provinceOptions,
    optionsState.countyOptions,
    control,
    setFilter,
    (IndicatorLevelId: any) =>
      setFilterState((prev) => ({
        ...prev,
        IndicatorLevelId: IndicatorLevelId,
      })),
    (ProvinceId: any) =>
      setFilterState((prev) => ({ ...prev, ProvinceId: ProvinceId })),
    (CountyId: any) =>
      setFilterState((prev) => ({ ...prev, CountyId: CountyId })),
    filterState.ProvinceId,
    filterState.IndicatorLevelId,
    levelLoaded,
    provinceLoaded,
    countyLoaded,
    user,
    receivedFilters,
  );

  const filterChildrenMainTable = (
    <SelectGroup
      selectComponents={selectComponentsMainTable}
      spaceBetween={2}
      flexSize={1}
    />
  );

  const setSelectedFilter = (filterName: string, value: string | null) => {
    setFilterState((prev) => ({ ...prev, [filterName]: value }));
  };

  useEffect(() => {
    updateSelectedFilterItemsName(
      filterState,
      filters,
      setSelectedFilterItems,
      filterNames.filter(
        (filterName) =>
          !["IndicatorLevelId", "ProvinceId", "CountyId"].includes(filterName),
      ),
    );
  }, [filterNames, filterState, filters]);

  const selectComponents = regionTableSelectFilterData(
    optionsState.subSystemOptions,
    optionsState.typeOptions,
    optionsState.stateOptions,
    control,
    setFilter,
    setSelectedFilter,
    subSystemLoaded,
    typeLoaded,
    stateLoaded,
  );

  const filterChildren = (
    <SelectGroup selectComponents={selectComponents} width="230px" />
  );

  const tableConfig: TableConfig = {
    tableName: "Lista de indicadores",
    dataTable: dataTableMonth,
    setDataTable: setDataTableMonth,
    renderTable: (dataTableMonth) => (
      <TableRegion
        frequency={3}
        dataTable={dataTableMonth}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
      />
    ),
    isLoaded,
    errorFindingData,
  };

  const tableConfigMonth: TableConfig = {
    tableName: "Lista de indicadores mensais",
    dataTable: dataTableMonth,
    setDataTable: setDataTableMonth,
    renderTable: (dataTableMonth) => (
      <TableRegion
        frequency={3}
        dataTable={dataTableMonth}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
      />
    ),
    isLoaded,
    errorFindingData: errorFindingDataMonth,
  };

  const {
    currentPage: currentPageMonth,
    totalPages: totalPagesMonth,
    handlePageChange: handlePageChangeMonth,
    renderContent: renderContentMonth,
  } = useTableRendering(
    tableConfigMonth,
    pageSize,
    totalItemsMonth,
    setIsSearching,
  );

  const paginationConfigMonth: PaginationConfig = {
    pageSize,
    currentPage: currentPageMonth,
    totalPages: totalPagesMonth,
    handlePageChange: handlePageChangeMonth,
    renderContent: renderContentMonth,
    setPageSize,
    totalItems: totalItemsMonth,
  };

  const tableConfigTrimester: TableConfig = {
    tableName: "Lista de indicadores trimestrais",
    dataTable: dataTableTrimester,
    setDataTable: setDataTableTrimester,
    renderTable: (dataTableTrimester) => (
      <TableRegion
        frequency={4}
        dataTable={dataTableTrimester}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
      />
    ),
    isLoaded,
    errorFindingData: errorFindingDataTrimester,
  };

  const {
    currentPage: currentPageTrimester,
    totalPages: totalPagesTrimester,
    handlePageChange: handlePageChangeTrimester,
    renderContent: renderContentTrimester,
  } = useTableRendering(
    tableConfigTrimester,
    pageSize,
    totalItemsTrimester,
    setIsSearching,
  );

  const paginationConfigTrimester: PaginationConfig = {
    pageSize,
    currentPage: currentPageTrimester,
    totalPages: totalPagesTrimester,
    handlePageChange: handlePageChangeTrimester,
    renderContent: renderContentTrimester,
    setPageSize,
    totalItems: totalItemsTrimester,
  };

  const tableConfigSemester: TableConfig = {
    tableName: "Lista de indicadores semestrais",
    dataTable: dataTableSemester,
    setDataTable: setDataTableSemester,
    renderTable: (dataTableSemester) => (
      <TableRegion
        frequency={5}
        dataTable={dataTableSemester}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
      />
    ),
    isLoaded,
    errorFindingData: errorFindingDataSemester,
  };

  const {
    currentPage: currentPageSemester,
    totalPages: totalPagesSemester,
    handlePageChange: handlePageChangeSemester,
    renderContent: renderContentSemester,
  } = useTableRendering(
    tableConfigSemester,
    pageSize,
    totalItemsSemester,
    setIsSearching,
  );

  const paginationConfigSemester: PaginationConfig = {
    pageSize,
    currentPage: currentPageSemester,
    totalPages: totalPagesSemester,
    handlePageChange: handlePageChangeSemester,
    renderContent: renderContentSemester,
    setPageSize,
    totalItems: totalItemsSemester,
  };

  const tableConfigYear: TableConfig = {
    tableName: "Lista de indicadores anuais",
    dataTable: dataTableYear,
    setDataTable: setDataTableYear,
    renderTable: (dataTableYear) => (
      <TableRegion
        frequency={6}
        dataTable={dataTableYear}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
      />
    ),
    isLoaded,
    errorFindingData: errorFindingDataYear,
  };

  const {
    currentPage: currentPageYear,
    totalPages: totalPagesYear,
    handlePageChange: handlePageChangeYear,
    renderContent: renderContentYear,
  } = useTableRendering(
    tableConfigYear,
    pageSize,
    totalItemsYear,
    setIsSearching,
  );

  const paginationConfigYear: PaginationConfig = {
    pageSize,
    currentPage: currentPageYear,
    totalPages: totalPagesYear,
    handlePageChange: handlePageChangeYear,
    renderContent: renderContentYear,
    setPageSize,
    totalItems: totalItemsYear,
  };

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "",
    "",
  );

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  const filterConfig: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    keepActiveFilters: keepActiveFiltersArray,
    selectedFilterItem: selectedFilterItems,
  };

  const filterRules = [
    () => !filtersArray[0]?.value,
    () => filtersArray[0]?.value === "4" && !filtersArray[1]?.value,
    () => filtersArray[0]?.value === "3" && !filtersArray[2]?.value,
  ];

  useEffect(() => {
    if (!isOpen) {
      resetField("StartPeriod");
      resetField("EndPeriod");
      resetField("StartYear");
      resetField("EndYear");
      setFormValues({
        frequency: null,
        province: null,
        county: null,
        indicator: null,
      });
    }
  }, [isOpen, resetField]);

  return (
    <>
      <AnalysisFilterTemplate
        title="Análise por regiões"
        summary={INDICATOR_REGION_ANALYSIS_SUMMARY}
        filterChildren={filterChildrenMainTable}
        filters={filtersArray}
        dataTable={dataTable}
        setFilteredTable={setDataTable}
        setShowTable={setShowTable}
        setIsSearching={setIsSearching}
        filterRules={filterRules}
        setIsLoaded={setRegionLoaded}
      />
      {showTable &&
        (!regionLoaded ? (
          <Box
            border={borderStyle}
            borderBottomRadius={borderRadius}
            padding="3"
          >
            {renderLoadingState()}
          </Box>
        ) : (
          <Flex direction="column">
            <Box
              p="4"
              backgroundColor="gray.300"
              border={borderStyle}
              borderRadius={borderRadius}
              borderTopRadius="0"
            >
              <RegionSearchTamplate
                setDataTable={setDataTable}
                dataTable={dataTable}
                modalConfig={modalConfig}
                filterConfig={filterConfig}
                setIsSearching={setIsSearching}
                filterState={filterState}
                searchTableName={
                  filterState.CountyId
                    ? `Análise ${filterState.ProvinceId?.name} / ${filterState.CountyId?.name}`
                    : `Análise ${filterState.ProvinceId?.name}`
                }
                control={control}
                historyTitle={filterState.ProvinceId?.name ?? ""}
                totalItems={
                  totalItemsMonth +
                  totalItemsSemester +
                  totalItemsTrimester +
                  totalItemsYear
                }
                setFormValues={setFormValues}
                formValues={formValues}
              />
            </Box>
            <Flex
              padding="4"
              backgroundColor="gray.50"
              gap="6"
              direction="column"
            >
              {totalPagesMonth > 0 && (
                <AccordionComponent accordionTitle="Indicadores mensais">
                  <Box border="none">
                    <RegionSearchTable
                      tableConfig={tableConfigMonth}
                      paginationConfig={paginationConfigMonth}
                    />
                  </Box>
                </AccordionComponent>
              )}
              {totalPagesTrimester > 0 && (
                <AccordionComponent accordionTitle="Indicadores trimestrais">
                  <Box border="none">
                    <RegionSearchTable
                      tableConfig={tableConfigTrimester}
                      paginationConfig={paginationConfigTrimester}
                    />
                  </Box>
                </AccordionComponent>
              )}
              {totalPagesSemester > 0 && (
                <AccordionComponent accordionTitle="Indicadores semestrais">
                  <Box border="none">
                    <RegionSearchTable
                      tableConfig={tableConfigSemester}
                      paginationConfig={paginationConfigSemester}
                    />
                  </Box>
                </AccordionComponent>
              )}
              {totalPagesYear > 0 && (
                <AccordionComponent accordionTitle="Indicadores anuais">
                  <Box border="none">
                    <RegionSearchTable
                      tableConfig={tableConfigYear}
                      paginationConfig={paginationConfigYear}
                    />
                  </Box>
                </AccordionComponent>
              )}
              {totalPagesMonth === 0 &&
                totalPagesTrimester === 0 &&
                totalPagesSemester === 0 &&
                totalPagesYear === 0 && (
                  <Box border="none">
                    <RegionSearchTable
                      tableConfig={tableConfig}
                      paginationConfig={paginationConfigMonth}
                    />
                  </Box>
                )}
            </Flex>
          </Flex>
        ))}
    </>
  );
};

export default RegionAnalysis;
